/**
 * Modal de edición de compensación — Sprint 17 Fase 2.
 *
 * Reglas de campos editables (alineadas con el servicio backend):
 * - lote / tenencia / categoria NUNCA editables (no se renderizan).
 * - Si la compensación tiene aplicaciones: descripcion, notas,
 *   vigente_desde y vigente_hasta. Las fechas de vigencia son metadata
 *   descriptiva (la aplicación greedy las ignora; solo afectan la grilla
 *   anual de Casas), por eso quedan editables aun con aplicaciones. Monto
 *   y periodicidad se muestran en modo lectura.
 * - Sin aplicaciones: todo editable salvo las 3 inmutables.
 */
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { actualizarCompensacion } from '../../lib/api/compensaciones';
import type {
  CompensacionConDetalle,
  CompensacionUpdate,
} from '../../lib/api/types';
import { Button } from '../ui/Button';
import { InputFecha } from '../ui/InputFecha';
import { Monto } from './Monto';

interface Props {
  compensacion: CompensacionConDetalle;
  onCerrar: () => void;
  onGuardada: () => void;
}

export function ModalEditarCompensacion({
  compensacion,
  onCerrar,
  onGuardada,
}: Props) {
  const tieneAplicaciones = compensacion.n_aplicaciones > 0;

  const [descripcion, setDescripcion] = useState(compensacion.descripcion);
  const [monto, setMonto] = useState(String(compensacion.monto));
  const [periodicidad, setPeriodicidad] = useState(compensacion.periodicidad);
  const [vigenteDesde, setVigenteDesde] = useState(compensacion.vigente_desde);
  const [vigenteHasta, setVigenteHasta] = useState(
    compensacion.vigente_hasta ?? '',
  );
  const [notas, setNotas] = useState(compensacion.notas ?? '');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: CompensacionUpdate = {
        descripcion: descripcion.trim(),
        vigente_desde: vigenteDesde,
        vigente_hasta: vigenteHasta || null,
        notas: notas.trim() || null,
      };
      if (!tieneAplicaciones) {
        payload.monto = Number.parseInt(monto, 10);
        payload.periodicidad = periodicidad;
      }
      await actualizarCompensacion(compensacion.id, payload);
      onGuardada();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' && err.body !== null && 'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Editar compensación"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Editar compensación
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="text-xs text-text-secondary border-l-2 border-text-muted/30 pl-3">
          Casa {compensacion.lote_identificador} ·{' '}
          {compensacion.vecino_nombre} · {compensacion.categoria_nombre}
        </div>

        {tieneAplicaciones && (
          <div className="bg-state-warning/[0.08] border-l-4 border-state-warning p-3 rounded-sm text-xs text-text-secondary">
            Esta compensación tiene{' '}
            <strong>{compensacion.n_aplicaciones} aplicaciones</strong>{' '}
            registradas. Descripción, vigencia (desde/hasta) y notas son
            editables — monto y periodicidad se bloquean para preservar la
            coherencia de gastos y pagos virtuales históricos.
          </div>
        )}

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Descripción</span>
          <input
            type="text"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              {periodicidad === 'unica' ? 'Monto total' : 'Monto por periodo'}
            </span>
            {tieneAplicaciones ? (
              <div className="bg-bg-sunken/50 border-[0.5px] border-white/10 rounded px-2 py-1.5 text-text-muted">
                <Monto value={compensacion.monto} />{' '}
                <span className="text-[10px]">(bloqueado)</span>
              </div>
            ) : (
              <input
                type="number"
                min={1}
                value={monto}
                onChange={(e) => setMonto(e.target.value)}
                className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
                required
              />
            )}
          </label>

          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Periodicidad</span>
            {tieneAplicaciones ? (
              <div className="bg-bg-sunken/50 border-[0.5px] border-white/10 rounded px-2 py-1.5 text-text-muted">
                {compensacion.periodicidad}{' '}
                <span className="text-[10px]">(bloqueado)</span>
              </div>
            ) : (
              <select
                value={periodicidad}
                onChange={(e) =>
                  setPeriodicidad(
                    e.target.value as CompensacionConDetalle['periodicidad'],
                  )
                }
                className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
                required
              >
                <option value="unica">única</option>
                <option value="mensual">mensual</option>
                <option value="bimestral">bimestral</option>
                <option value="semestral">semestral</option>
                <option value="anual">anual</option>
              </select>
            )}
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Vigente desde</span>
            <InputFecha
              value={vigenteDesde}
              onChange={setVigenteDesde}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              requerido
            />
          </label>

          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Vigente hasta (opcional)</span>
            <InputFecha
              value={vigenteHasta}
              onChange={setVigenteHasta}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            />
          </label>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Notas</span>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={2}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </form>
    </div>
  );
}
